import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { zohoAPI } from '@/lib/api';
import SubmKPI from '@/components/zoho/submissions/SubmKPI';
import SubmFilters from '@/components/zoho/submissions/SubmFilters';
import SubmGrid from '@/components/zoho/submissions/SubmGrid';
import SubmTable from '@/components/zoho/submissions/SubmTable';
import SubmDetail from '@/components/zoho/submissions/SubmDetail';

export default function SubmDashboard() {
  var params = useParams();
  var navigate = useNavigate();
  var formId = params.formId;

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var formState = useState(null);
  var form = formState[0];
  var setForm = formState[1];

  var entitiesState = useState([]);
  var entities = entitiesState[0];
  var setEntities = entitiesState[1];

  var metaState = useState({});
  var meta = metaState[0];
  var setMeta = metaState[1];

  var viewState = useState('grid');
  var viewMode = viewState[0];
  var setViewMode = viewState[1];

  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var debouncedSearchState = useState('');
  var debouncedSearch = debouncedSearchState[0];
  var setDebouncedSearch = debouncedSearchState[1];

  var debounceRef = useRef(null);

  function handleSearch(val) {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function() { setDebouncedSearch(val); }, 400);
  }

  var monthState = useState('all');
  var month = monthState[0];
  var setMonth = monthState[1];

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var fetchData = useCallback(async function() {
    setLoading(true);
    try {
      var formRes = await zohoAPI.getForm(formId);
      var formData = formRes.data;
      setForm(formData);

      var entityParams = { exclude_test: true };
      if (debouncedSearch) entityParams.search = debouncedSearch;
      if (month && month !== 'all') entityParams.month = month;

      var entRes = await zohoAPI.getFormEntities(formId, entityParams);
      var data = entRes.data;
      setEntities(data.entities || []);
      setMeta({
        pkField: data.primary_key_field,
        nameField: data.display_name_field,
        periodField: data.period_field,
        total: data.total,
      });
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [formId, debouncedSearch, month]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  // Collect all unique field names from raw_payload
  var allFields = useMemo(function() {
    var fieldSet = {};
    for (var i = 0; i < entities.length; i++) {
      var subs = entities[i].submissions || [];
      for (var j = 0; j < subs.length; j++) {
        var rp = subs[j].raw_payload || {};
        var keys = Object.keys(rp);
        for (var k = 0; k < keys.length; k++) {
          fieldSet[keys[k]] = true;
        }
      }
    }
    return Object.keys(fieldSet);
  }, [entities]);

  function handleExport() {
    toast.info('Export functionality coming soon');
  }

  async function handleApprove(subId) {
    try {
      await zohoAPI.approveSubmission(subId);
      toast.success('Submission accepted');
      setSelected(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to approve submission');
    }
  }

  async function handleReject(subId) {
    try {
      await zohoAPI.rejectSubmission(subId);
      toast.success('Submission rejected');
      setSelected(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to reject submission');
    }
  }

  var formName = form ? (form.name || form.form_name || 'Form') : 'Loading...';
  var pkField = meta.pkField || '';
  var nameField = meta.nameField || '';

  if (loading && !form) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="subm-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subm-dashboard-page">
      <div className="max-w-[1600px] mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { navigate(-1); }} data-testid="subm-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{formName} Submissions</h1>
              <p className="text-sm text-muted-foreground">
                {meta.total || 0} entities &middot; Primary Key: {pkField}
              </p>
            </div>
          </div>
          {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        </div>

        <SubmKPI entities={entities} />

        <SubmFilters
          viewMode={viewMode}
          onViewChange={setViewMode}
          search={search}
          onSearch={handleSearch}
          month={month}
          onMonth={setMonth}
          onExport={handleExport}
        />

        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-semibold text-slate-700">{entities.length}</span> entities
            </p>
          </div>

          {viewMode === 'grid' ? (
            <SubmGrid entities={entities} pkField={pkField} nameField={nameField} onSelect={setSelected} onApprove={handleApprove} onReject={handleReject} />
          ) : (
            <SubmTable entities={entities} pkField={pkField} nameField={nameField} allFields={allFields} onSelect={setSelected} onApprove={handleApprove} onReject={handleReject} />
          )}
        </div>

        <SubmDetail
          entity={selected}
          open={selected !== null}
          onClose={function() { setSelected(null); }}
          onApprove={handleApprove}
          onReject={handleReject}
          onRefresh={function() { setSelected(null); fetchData(); }}
        />
      </div>
    </div>
  );
}
